import { useCallback, useMemo } from 'react';
import { parsePartIds } from '../../common/chat/members/memberListUtils';
import {
  buildChatMentionCandidates,
  normalizeMentionTargetId,
  normalizeMentionTargetRole,
  normalizeMentionTargetType,
} from '../../common/chat/mentions/chatMentionUtils';
import {
  buildGroupArtistSectionsForModal,
  buildPartsByIdMap,
} from '../../common/chat/members/memberModalSharedDataUtils';
import { resolveGroupArtistPartValues } from '../../common/chat/members/groupArtistPartUtils';
import {
  buildAdminProfileForModal,
  buildArtistProfileForModal,
} from '../../common/chat/members/memberModalProfileUtils';
import {
  buildGroupAdminProfilePayload,
  buildGroupArtistProfilePayload,
} from '../../common/chat/members/groupProfilePayloadUtils';
import { resolveGroupAvatarProfileTarget } from '../../common/chat/avatars/groupAvatarProfileTargetUtils';
import { pickFirstString } from './artistGroupChatPaneUtils';

export default function useArtistGroupChatMembers({
  artistUserId,
  openAdminProfile,
  openArtistProfile,
  openProfileTarget,
  parts,
  setIsMemberModalOpen,
  thread,
}) {
  const members = useMemo(() => ({
    assignees: Array.isArray(thread?.assignees) ? thread.assignees : [],
    artists: Array.isArray(thread?.artists) ? thread.artists : [],
  }), [thread]);

  const partsByIdForGrouping = useMemo(() => buildPartsByIdMap(parts), [parts]);

  const groupedSidePanelArtists = useMemo(() => {
    const sections = buildGroupArtistSectionsForModal({
      artists: members.artists,
      partsById: partsByIdForGrouping,
      parsePartIds,
      pickFirstStringFn: pickFirstString,
      resolvePartIdsFn: resolveGroupArtistPartValues,
    });
    return sections.map((section) => ({
      key: section.id,
      label: section.title || '未設定',
      members: Array.isArray(section.members) ? section.members : [],
    }));
  }, [members.artists, partsByIdForGrouping]);

  const artistMemberLookup = useMemo(() => {
    const map = new Map();
    members.artists.forEach((artist) => {
      const artistId = String(artist?.artist_id || artist?.id || artist?.artist_user_id || '').trim();
      if (!artistId || map.has(artistId)) return;
      map.set(artistId, artist);
    });
    return map;
  }, [members.artists]);

  const assigneeLookup = useMemo(() => {
    const map = new Map();
    members.assignees.forEach((admin) => {
      const adminId = String(admin?.id || admin?.admin_user_id || '').trim();
      if (!adminId || map.has(adminId)) return;
      map.set(adminId, admin);
    });
    return map;
  }, [members.assignees]);

  const mentionCandidates = useMemo(() => buildChatMentionCandidates({
    admins: Array.from(assigneeLookup.values()),
    artists: Array.from(artistMemberLookup.values()),
    parts,
    currentUser: { role: 'artist', id: artistUserId },
  }), [artistMemberLookup, artistUserId, assigneeLookup, parts]);

  const participantCount = members.assignees.length + members.artists.length;

  const buildArtistProfileData = useCallback((source) => {
    if (!source) return null;
    return buildArtistProfileForModal({
      artist: buildGroupArtistProfilePayload({
        source,
        pickFirstString,
        resolvePartLabel: (artist) =>
          pickFirstString(artist.part, artist.part_label, artist.main_part_label),
        defaultName: 'アーティスト',
      }),
    });
  }, []);

  const buildAdminProfileData = useCallback((source) => {
    if (!source) return null;
    return buildAdminProfileForModal({
      admin: buildGroupAdminProfilePayload({
        source,
        pickFirstString,
        defaultName: '管理者',
      }),
    });
  }, []);

  const handleAvatarClick = useCallback((message) => {
    const target = resolveGroupAvatarProfileTarget({
      message,
      artistLookup: artistMemberLookup,
      assigneeLookup,
      buildArtistProfile: buildArtistProfileData,
      buildAdminProfile: buildAdminProfileData,
    });
    openProfileTarget(target);
  }, [
    artistMemberLookup,
    assigneeLookup,
    buildAdminProfileData,
    buildArtistProfileData,
    openProfileTarget,
  ]);

  const handleMentionClick = useCallback(
    (mention) => {
      const mentionType = normalizeMentionTargetType(mention);
      if (mentionType === 'part') {
        setIsMemberModalOpen(true);
        return;
      }

      const role = normalizeMentionTargetRole(mention);
      const targetId = normalizeMentionTargetId(mention);
      const artist = artistMemberLookup.get(targetId);
      const admin = assigneeLookup.get(targetId);
      if (role === 'admin' && admin) {
        openAdminProfile(buildAdminProfileData(admin));
        return;
      }
      if (role === 'artist' && artist) {
        openArtistProfile(buildArtistProfileData(artist));
        return;
      }
      if (artist) openArtistProfile(buildArtistProfileData(artist));
      else if (admin) openAdminProfile(buildAdminProfileData(admin));
    },
    [
      artistMemberLookup,
      assigneeLookup,
      buildAdminProfileData,
      buildArtistProfileData,
      openAdminProfile,
      openArtistProfile,
      setIsMemberModalOpen,
    ]
  );

  return {
    buildAdminProfileData,
    buildArtistProfileData,
    groupedSidePanelArtists,
    handleAvatarClick,
    handleMentionClick,
    members,
    mentionCandidates,
    participantCount,
  };
}
